import * as React from 'react';
import styled from 'styled-components';
import { ANIMATION, BREAK_POINTS, TEXT } from '../../consts/style';
import { HeadingLink, headingLinkOnHoverMixin } from '../Text';
import { MaxWidthImage, MaxWidths } from '../Image/MaxWidthImage';
import { DangerousHtml } from '../DangerousHtml';
import { IDamImage } from '../../models/content.interfaces';
import { Link } from '../Link';

interface CardBlockProps {
  twoColumns?: boolean;
  image: IDamImage;
  header: string;
  byline: string;
  link?: string;
  trackingPrefix: string;
}

const CardLink = styled(Link)`
  background: white;
  box-sizing: border-box;
  width: 100%;
  margin: 0 auto 8px auto;
  vertical-align: top;
  color: black;
  text-decoration: none;
  cursor: pointer;

  ${headingLinkOnHoverMixin};

  @media (min-width: 576px) {
    display: flex;
    height: fit-content;
    flex: 1;
    margin: 0 4px;

    flex-wrap: wrap;

    &:first-child {
      margin-left: 0;
    }
    &:last-child {
      margin-right: 0;
    }
  }

  @media (min-width: 768px) {
    margin: 0 8px;
  }

  @media (min-width: 992px) {
    margin: 0 15px;
  }

  @media (min-width: ${BREAK_POINTS.MIN.XS}) {
    &:hover img {
      transform: scale(${ANIMATION.CARD.SCALE});
    }
  }

  transition: all ${ANIMATION.CARD.SPEED};
  &:hover {
    box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.16);
  }
` as any;

const ImageContainer = styled.div`
  position: relative;
  display: block;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: ${BREAK_POINTS.MAX.XS}) {
    width: 104px;
    height: 104px;
    margin: 24px 16px;
    float: right;

    & > img {
      height: 100%;
      position: absolute;
      left: 50%;
      top: 50%;
      -moz-transform: translateY(-50%) translateX(-50%);
      -webkit-transform: translateY(-50%) translateX(-50%);
      transform: translateY(-50%) translateX(-50%);
      transition: all ${ANIMATION.CARD.SPEED};
    }
  }

  @media (min-width: ${BREAK_POINTS.MIN.XS}) {
    height: 102px;
    width: 100%;
    padding: 0;
    margin: 0;

    & > img {
      width: 100%;
      left: 0;
      top: ${(p: CardBlockProps) => (p.twoColumns ? '-50%' : '0')};
      float: unset;
      -moz-transform: translateY(0) translateX(0);
      -webkit-transform: translateY(0) translateX(0);
      transform: translateY(0) translateX(0);
      transition: all ${ANIMATION.CARD.SPEED};

      position: relative;
    }
  }

  @media (min-width: ${BREAK_POINTS.MIN.SM}) {
    height: 133px;
  }
  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    height: 165px;
  }
  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    height: 203px;
  }
` as any;

const TextContainer = styled.div`
  padding: 22px 16px;
  height: 104px;
  overflow: hidden;

  > div {
    display: block;
    display: -webkit-box;
    margin: 0 auto;
    font-size: 14px;
    line-height: 1.71;
    -webkit-line-clamp: 5;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > div > span {
    font-weight: normal;
    font-size: 14px;
    font-weight: normal;
    line-height: 1.71;
    color: ${TEXT.BODY};
  }

  @media (min-width: 576px) {
    width: 100%;
    vertical-align: top;
  }
`;

const cardImageMaxWidths: MaxWidths = {
  XS: 185,
  SM: 255,
  MD: 365,
  LG: 365,
};

export const CardBlock = ({
  twoColumns,
  image,
  header,
  byline,
  link,
  trackingPrefix,
}: CardBlockProps) => {
  return (
    <CardLink
      data-comment="CardBlock"
      data-id={`${trackingPrefix}-${header}`}
      href={link}
      componentType="link"
    >
      <ImageContainer twoColumns={twoColumns} data-comment="ImageContainer">
        <MaxWidthImage maxWidths={cardImageMaxWidths} alt={header} image={image} />
      </ImageContainer>
      <TextContainer data-comment="TextContainer">
        <div>
          <HeadingLink inline>{header}</HeadingLink>
          <br />
          <br />
          <DangerousHtml html={byline} />
        </div>
      </TextContainer>
    </CardLink>
  );
};
